/**
 * Performance utility functions
 */

/**
 * Debounce function to limit the rate of function calls
 */
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(this, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(this, args);
  };
}

/**
 * Throttle function to limit function calls to once per specified time period
 */
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Intersection Observer for lazy loading
 */
export class LazyLoader {
  constructor(options = {}) {
    this.options = {
      root: null,
      rootMargin: "50px",
      threshold: 0.1,
      ...options,
    };
    this.observer = null;
    this.callbacks = new WeakMap();
  }

  init() {
    if (!this.observer && "IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), this.options);
    }
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const callback = this.callbacks.get(entry.target);
        if (callback) {
          callback(entry.target);
          this.unobserve(entry.target);
        }
      }
    });
  }

  observe(element, callback) {
    if (!this.observer) this.init();
    if (this.observer) {
      this.callbacks.set(element, callback);
      this.observer.observe(element);
    } else {
      // Fallback for browsers without IntersectionObserver
      callback(element);
    }
  }

  unobserve(element) {
    if (this.observer) {
      this.observer.unobserve(element);
      this.callbacks.delete(element);
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.callbacks = new WeakMap();
    }
  }
}

/**
 * Image lazy loading utility
 */
export function setupImageLazyLoading(container = document) {
  const lazyLoader = new LazyLoader({
    rootMargin: "100px",
  });

  const images = container.querySelectorAll("img[data-src]");

  images.forEach((img) => {
    lazyLoader.observe(img, (element) => {
      const src = element.getAttribute("data-src");
      if (src) {
        element.src = src;
        element.removeAttribute("data-src");
        element.classList.add("loaded");
      }
    });
  });

  return lazyLoader;
}

/**
 * Memoization utility for expensive computations
 */
export function memoize(fn, getKey = (...args) => JSON.stringify(args)) {
  const cache = new Map();

  return function memoized(...args) {
    const key = getKey(...args);

    if (cache.has(key)) {
      return cache.get(key);
    }

    const result = fn.apply(this, args);
    cache.set(key, result);

    return result;
  };
}

/**
 * Virtual scrolling utility for large lists
 */
export class VirtualScroller {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      itemHeight: 100,
      buffer: 5,
      ...options,
    };
    this.items = [];
    this.visibleItems = [];
    this.scrollTop = 0;
    this.containerHeight = 0;

    this.init();
  }

  init() {
    this.container.style.overflow = "auto";
    this.container.addEventListener("scroll", this.handleScroll.bind(this));
    this.updateContainerHeight();
    window.addEventListener(
      "resize",
      debounce(() => {
        this.updateContainerHeight();
        this.render();
      }, 100),
    );
  }

  updateContainerHeight() {
    this.containerHeight = this.container.clientHeight;
  }

  setItems(items) {
    this.items = items;
    this.render();
  }

  handleScroll() {
    this.scrollTop = this.container.scrollTop;
    this.render();
  }

  render() {
    const startIndex = Math.max(
      0,
      Math.floor(this.scrollTop / this.options.itemHeight) - this.options.buffer,
    );
    const endIndex = Math.min(
      this.items.length - 1,
      Math.ceil((this.scrollTop + this.containerHeight) / this.options.itemHeight) +
        this.options.buffer,
    );

    this.visibleItems = this.items.slice(startIndex, endIndex + 1).map((item, index) => ({
      ...item,
      index: startIndex + index,
      top: (startIndex + index) * this.options.itemHeight,
    }));

    // Update container height to maintain scroll behavior
    const totalHeight = this.items.length * this.options.itemHeight;
    this.container.style.height = `${totalHeight}px`;

    // Emit render event with visible items
    this.container.dispatchEvent(
      new CustomEvent("virtualRender", {
        detail: {
          visibleItems: this.visibleItems,
          startIndex,
          endIndex,
        },
      }),
    );
  }

  destroy() {
    window.removeEventListener("resize", this.handleScroll);
    this.container.removeEventListener("scroll", this.handleScroll);
  }
}

/**
 * Performance monitoring utility
 */
export class PerformanceMonitor {
  constructor() {
    this.marks = new Map();
    this.measures = new Map();
  }

  mark(name) {
    if ("performance" in window && performance.mark) {
      performance.mark(name);
      this.marks.set(name, performance.now());
    }
  }

  measure(name, startMark, endMark) {
    if ("performance" in window && performance.measure) {
      try {
        performance.measure(name, startMark, endMark);
        const measure = performance.getEntriesByName(name, "measure")[0];
        this.measures.set(name, measure.duration);
        return measure.duration;
      } catch (error) {
        console.warn("Performance measurement failed:", error);
      }
    }
    return null;
  }

  getMetrics() {
    return {
      marks: Object.fromEntries(this.marks),
      measures: Object.fromEntries(this.measures),
    };
  }

  clear() {
    if ("performance" in window) {
      performance.clearMarks();
      performance.clearMeasures();
    }
    this.marks.clear();
    this.measures.clear();
  }
}

// Create singleton instances
export const lazyLoader = new LazyLoader();
export const performanceMonitor = new PerformanceMonitor();

/**
 * Enhanced device detection with performance considerations
 */
export const deviceUtils = {
  isMobile: () => window.innerWidth <= 768,
  isTablet: () => window.innerWidth > 768 && window.innerWidth <= 1024,
  isDesktop: () => window.innerWidth > 1024,

  // Cached device check to avoid repeated calculations
  _cachedDeviceType: null,
  _lastCheckTime: 0,

  getDeviceType() {
    const now = Date.now();
    if (this._cachedDeviceType && now - this._lastCheckTime < 1000) {
      return this._cachedDeviceType;
    }

    if (this.isMobile()) {
      this._cachedDeviceType = "mobile";
    } else if (this.isTablet()) {
      this._cachedDeviceType = "tablet";
    } else {
      this._cachedDeviceType = "desktop";
    }

    this._lastCheckTime = now;
    return this._cachedDeviceType;
  },

  // Invalidate cache on resize
  invalidateCache() {
    this._cachedDeviceType = null;
  },
};

// Setup device cache invalidation
window.addEventListener(
  "resize",
  debounce(() => {
    deviceUtils.invalidateCache();
  }, 100),
);
